/**
 * gbi-swipe-stack — Tinder-style swipe deck.
 *
 * Usage: <div data-swipe-stack><div class="card">...</div>...</div>
 * Drag top card; if dx > threshold, slide out and cycle next.
 * Reduced-motion: shows top card statically + injects prev/next buttons.
 */
(function () {
  'use strict';

  const THRESHOLD = 100;
  const MAX_ROTATE = 15;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function makeButton(label, ariaLabel, side) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    btn.setAttribute('aria-label', ariaLabel);
    btn.style.position = 'absolute';
    btn.style.top = '50%';
    btn.style.transform = 'translateY(-50%)';
    btn.style.background = '#1A1008';
    btn.style.color = '#FAFAF8';
    btn.style.border = '0';
    btn.style.borderRadius = '999px';
    btn.style.width = '44px';
    btn.style.height = '44px';
    btn.style.fontSize = '18px';
    btn.style.cursor = 'pointer';
    btn.style.zIndex = '99';
    btn.style[side] = '-56px';
    return btn;
  }

  function attach(stack) {
    const cards = Array.from(stack.querySelectorAll('.card'));
    if (cards.length === 0) return;

    let order = cards.slice();
    // Match Framer ScrollSplit.tsx MobileTinderStack offsets
    const STACK_SCALES = [1, 0.92, 0.85];
    const STACK_OFFSETS = [0, 12, 24];
    let onAfterRestack = null;

    function restack() {
      order.forEach((c, i) => {
        c.style.zIndex = String(order.length * 10 - i * 10);
        const scale = STACK_SCALES[i] != null ? STACK_SCALES[i] : 0.8;
        const offsetY = STACK_OFFSETS[i] != null ? STACK_OFFSETS[i] : 32;
        c.style.transform = i === 0
          ? 'translate(0, 0)'
          : `translate(0, ${offsetY}px) scale(${scale})`;
        c.style.transition = 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)';
        c.style.pointerEvents = i === 0 ? 'auto' : 'none';
        c.style.opacity = i < 3 ? '1' : '0';
      });
      if (onAfterRestack) onAfterRestack();
    }

    function dealIn() {
      order.forEach((c, i) => {
        c.style.transition = 'none';
        c.style.transform = 'translate(0, 0) scale(0.8)';
        c.style.opacity = '0';
      });
      // Force reflow then apply staggered reveal
      void stack.offsetWidth;
      order.forEach((c, i) => {
        const delay = i * 0.08;
        c.style.transition = `transform 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, opacity 0.4s ease ${delay}s`;
      });
      requestAnimationFrame(restack);
    }
    restack();

    if (reduceMotion) {
      stack.style.position = 'relative';
      const prev = makeButton('←', 'Previous card', 'left');
      const next = makeButton('→', 'Next card', 'right');
      stack.appendChild(prev);
      stack.appendChild(next);
      prev.addEventListener('click', () => { order.unshift(order.pop()); restack(); });
      next.addEventListener('click', () => { order.push(order.shift()); restack(); });
      return;
    }

    let dragging = false;
    let startX = 0, startY = 0, dx = 0, dy = 0;
    let activeCard = null;
    let cardWidth = 320;

    function measureCardWidth() {
      const top = order[0];
      cardWidth = top ? top.getBoundingClientRect().width || 320 : 320;
    }

    function onPointerDown(e) {
      const top = order[0];
      if (!top.contains(e.target)) return;
      dragging = true;
      activeCard = top;
      measureCardWidth();
      startX = e.clientX;
      startY = e.clientY;
      dx = 0; dy = 0;
      activeCard.style.transition = 'none';
      // Disable transitions on back cards too so drag-driven scale/y is instant
      order.forEach((c, i) => {
        if (i > 0) c.style.transition = 'transform 0.12s ease, opacity 0.4s ease';
      });
      try { activeCard.setPointerCapture(e.pointerId); } catch (_) {}
    }

    function onPointerMove(e) {
      if (!dragging) return;
      dx = e.clientX - startX;
      dy = e.clientY - startY;
      // Claim the gesture as a horizontal swipe once dx clearly dominates dy.
      // This stops the browser from interpreting it as a vertical pan / page
      // scroll mid-swipe. Requires the listener to be passive:false.
      if (Math.abs(dx) > 4 && Math.abs(dx) > Math.abs(dy)) {
        if (e.cancelable) e.preventDefault();
      }
      const rot = Math.max(-MAX_ROTATE, Math.min(MAX_ROTATE, dx / 10));
      // Front card: translate + rotate + opacity feedback
      const absDx = Math.abs(dx);
      const opacity = Math.max(0, 1 - absDx / (cardWidth * 1.5));
      activeCard.style.transform = `translate(${dx}px, ${dy * 0.2}px) rotate(${rot}deg)`;
      activeCard.style.opacity = String(opacity);

      // Back cards: scale UP and y-offset shrinks as front card moves away
      const dragInfluence = Math.min(absDx / cardWidth, 1);
      order.forEach((c, i) => {
        if (i === 0) return;
        const baseScale = STACK_SCALES[i] != null ? STACK_SCALES[i] : 0.8;
        const baseY = STACK_OFFSETS[i] != null ? STACK_OFFSETS[i] : 32;
        const scale = baseScale + dragInfluence * 0.08;
        const yPos = baseY - dragInfluence * 12;
        c.style.transform = `translate(0, ${yPos}px) scale(${scale})`;
      });
    }

    function onPointerUp() {
      if (!dragging) return;
      dragging = false;
      const past = Math.abs(dx) > THRESHOLD;
      activeCard.style.transition = 'transform 0.5s cubic-bezier(0.5, 0, 0.75, 0), opacity 0.4s ease';
      if (past) {
        const flyX = dx > 0 ? window.innerWidth : -window.innerWidth;
        const rot = dx > 0 ? 30 : -30;
        activeCard.style.transform = `translate(${flyX}px, ${dy}px) rotate(${rot}deg) scale(0.9)`;
        activeCard.style.opacity = '0';
        setTimeout(() => {
          order.push(order.shift());
          if (activeCard) {
            activeCard.style.transition = 'none';
            activeCard.style.transform = '';
            activeCard.style.opacity = '';
          }
          requestAnimationFrame(restack);
        }, 400);
      } else {
        activeCard.style.transform = '';
        activeCard.style.opacity = '';
        // Restore back-cards to base positions
        order.forEach((c, i) => {
          if (i === 0) return;
          const scale = STACK_SCALES[i] != null ? STACK_SCALES[i] : 0.8;
          const yPos = STACK_OFFSETS[i] != null ? STACK_OFFSETS[i] : 32;
          c.style.transform = `translate(0, ${yPos}px) scale(${scale})`;
        });
      }
      activeCard = null;
    }

    stack.addEventListener('pointerdown', onPointerDown);
    // passive:false so pointermove can call preventDefault() — needed
    // when touch-action falls back to the browser claiming the gesture.
    stack.addEventListener('pointermove', onPointerMove, { passive: false });
    stack.addEventListener('pointerup', onPointerUp);
    stack.addEventListener('pointercancel', onPointerUp);
    stack.addEventListener('lostpointercapture', onPointerUp);

    // Wire dot indicators if a sibling .swipe-stack-dots exists
    const dotsContainer = stack.parentElement && stack.parentElement.querySelector('[data-swipe-dots]');
    if (dotsContainer) {
      const dots = Array.from(dotsContainer.querySelectorAll('[data-swipe-dot]'));
      const syncDots = function () {
        const activeIdx = cards.indexOf(order[0]);
        dots.forEach((d, i) => d.toggleAttribute('data-active', i === activeIdx));
      };
      onAfterRestack = syncDots;
      syncDots();
      dots.forEach((d, idx) => {
        d.addEventListener('click', () => {
          while (cards.indexOf(order[0]) !== idx) order.push(order.shift());
          restack();
        });
      });
    }

    // Deal-in animation + wiggle hint when stack enters viewport
    if ('IntersectionObserver' in window) {
      const hint = stack.parentElement && stack.parentElement.querySelector('[data-swipe-hint]');
      const dealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(dealIn, 300);
            if (hint) {
              setTimeout(() => hint.setAttribute('data-active', ''), 1400);
              setTimeout(() => hint.removeAttribute('data-active'), 2400);
            }
            dealObserver.disconnect();
          }
        });
      }, { threshold: 0.3 });
      dealObserver.observe(stack);
    }
  }

  function init(root) {
    (root || document).querySelectorAll('[data-swipe-stack]:not([data-swipe-init])').forEach((el) => {
      el.setAttribute('data-swipe-init', '');
      attach(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  window.GBI = window.GBI || {};
  window.GBI.swipeStack = { init };
})();
